import dayjs from 'dayjs';
import React, { useState } from 'react';

import { Badge } from '@/components';
import { SiSlotSummary } from '@/services/api/scheduled-instant';

import { AGG, Panel, readyShare } from './shared';

/**
 * Slot Harian table view.
 *
 * Extracted from index.tsx (which was 1685 lines) so the new "Siap Konfirmasi"
 * column and the Screen 15 work are not another branch inside a monolith.
 *
 * "Siap Konfirmasi" needs no backend work: `counts.green` (CONFIRMED + REPLACED
 * + COMPLETED) and `riderCount` are already computed server-side by
 * list-slots.usecase.ts and were already being rendered here as a bare count
 * badge. This just states the ratio ops actually reads off it.
 */
export const TableView: React.FC<{
  slots: SiSlotSummary[];
  onChanged: () => void;
  onEditSlot: (slotId: string) => void;
  renderRiderPanel: (slotId: string) => React.ReactNode;
}> = ({ slots, onEditSlot, renderRiderPanel }) => {
  const [openId, setOpenId] = useState<string | null>(null);
  void onEditSlot;

  return (
    <Panel>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[10px] uppercase tracking-wide text-gray-400 border-b border-gray-100">
            <th className="text-left px-4 py-2.5 font-semibold">Tanggal</th>
            <th className="text-left px-4 py-2.5 font-semibold">Jam</th>
            <th className="text-left px-4 py-2.5 font-semibold">Klien</th>
            <th className="text-left px-4 py-2.5 font-semibold">Rider</th>
            <th className="text-left px-4 py-2.5 font-semibold">
              Siap Konfirmasi
            </th>
            <th className="text-left px-4 py-2.5 font-semibold">Status</th>
            {/* Toleransi is still a hardcoded placeholder — deferred to
                TODOS.md TODO-6, not fixed here. */}
            <th className="text-left px-4 py-2.5 font-semibold">Toleransi</th>
            <th className="px-4 py-2.5" />
          </tr>
        </thead>
        <tbody>
          {slots.map((s) => {
            const open = openId === s.slot.id;
            const share = readyShare(s.counts.green, s.riderCount);
            return (
              <React.Fragment key={s.slot.id}>
                <tr
                  className={`border-b border-gray-50 cursor-pointer transition-colors ${
                    open ? 'bg-(--theme-accent-alpha-10)' : 'hover:bg-gray-50'
                  }`}
                  onClick={() => setOpenId(open ? null : s.slot.id)}
                >
                  <td className="px-4 py-3 text-gray-600 tabular">
                    {dayjs(s.slot.scheduleDate).format('DD MMM')}
                  </td>
                  <td className="px-4 py-3 font-semibold text-ink tabular">
                    {s.slot.windowStartTime}–{s.slot.windowEndTime}
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-medium text-ink">
                      {s.slot.clientName}
                    </span>
                    {s.slot.batchLabel && (
                      <span className="text-gray-400 text-xs">
                        {' '}
                        · {s.slot.batchLabel}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {s.riderCount === 0 ? (
                      <Badge tone="amber" dot>
                        Belum ada rider
                      </Badge>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500 text-xs tabular">
                          {s.riderCount} rider
                        </span>
                        {s.dayOffCount > 0 && (
                          <Badge tone="warning" size="small" tabular>
                            <i className="ri-moon-line mr-0.5" />
                            {s.dayOffCount} day-off
                          </Badge>
                        )}
                        {s.counts.red > 0 && (
                          <Badge tone="danger" size="small" tabular>
                            {s.counts.red}
                          </Badge>
                        )}
                        {s.counts.amber > 0 && (
                          <Badge tone="amber" size="small" tabular>
                            {s.counts.amber}
                          </Badge>
                        )}
                        {s.counts.green > 0 && (
                          <Badge tone="success" size="small" tabular>
                            {s.counts.green}
                          </Badge>
                        )}
                      </div>
                    )}
                  </td>
                  {/* Siap Konfirmasi — em dash for an unstaffed slot, because 0%
                      would read as "riders assigned but none confirmed". */}
                  <td className="px-4 py-3" data-testid="siap-konfirmasi">
                    {share === null ? (
                      <span className="text-gray-300">—</span>
                    ) : (
                      <div className="flex items-baseline gap-1.5">
                        <span
                          className={`font-semibold tabular ${
                            share === 1
                              ? 'text-green-600'
                              : share >= 0.5
                                ? 'text-amber-600'
                                : 'text-red-600'
                          }`}
                        >
                          {Math.round(share * 100)}%
                        </span>
                        <span className="text-[11px] text-gray-400 tabular">
                          {s.counts.green} dari {s.riderCount}
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Badge
                      tone={AGG[s.aggregateStatus].tone}
                      dot
                      pulse={s.aggregateStatus === 'no_response'}
                    >
                      {AGG[s.aggregateStatus].label}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone="danger" size="small">
                      Zero
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-gray-400">
                    <i className={`ri-arrow-${open ? 'up' : 'down'}-s-line`} />
                  </td>
                </tr>
                {open && (
                  <tr>
                    <td colSpan={8} className="bg-gray-50 p-0">
                      {renderRiderPanel(s.slot.id)}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </Panel>
  );
};
